import { parseLocalDate, type YMD } from "@/tools/converters/dates";
import { parseNumber } from "@/tools/converters/numberInput";
import { US_STATES, longDate, todayIso } from "./legalClauses";

/**
 * Pure field checks shared by the lease, power of attorney, eviction notice, offer letter and cover letter tools.
 * Each returns a message for the field, or null when the value is fine.
 */

const norm = (s: string) => s.toLowerCase().replace(/[.,;:!?'\u2019"()\-_]/g, " ").replace(/\s+/g, " ").trim();
export const sameName = (a: string, b: string) => !!norm(a) && norm(a) === norm(b);

const cmp = (a: YMD, b: YMD) => (a.y - b.y) || (a.m - b.m) || (a.d - b.d);
const todayYmd = (now?: string): YMD => parseLocalDate(now ?? todayIso())!;

/** Long date ("December 1, 2026") from a yyyy-mm-dd value; the raw text if it does not parse. */
export function longDateText(iso: string): string {
  const d = parseLocalDate((iso ?? "").trim());
  return d ? longDate(d) : (iso ?? "").trim();
}

// ---------------------------------------------------------------- power of attorney
export interface PoaProblems { agent?: string; alternate?: string; expiration?: string }

export function poaProblems(i: { principal: string; agent: string; alternate: string; expiration: string }, now?: string): PoaProblems {
  const out: PoaProblems = {};
  if (sameName(i.principal, i.agent)) out.agent = "The agent must be someone other than you (the principal).";
  if (i.alternate.trim()) {
    if (sameName(i.principal, i.alternate)) out.alternate = "The alternate agent cannot be you (the principal).";
    else if (sameName(i.agent, i.alternate)) out.alternate = "The alternate agent must be a different person from the agent.";
  }
  if (i.expiration.trim()) {
    const e = parseLocalDate(i.expiration.trim());
    if (!e) out.expiration = "Enter a valid date.";
    else if (cmp(e, todayYmd(now)) <= 0) out.expiration = "The expiration date must be after today.";
  }
  return out;
}

// ---------------------------------------------------------------- eviction
export function positiveAmountProblem(raw: string, label = "Amount"): string | null {
  if (!raw.trim()) return null;
  const n = parseNumber(raw);
  if (n === null || !Number.isFinite(n)) return `Enter ${label.toLowerCase()} as a number.`;
  if (n <= 0) return `${label} must be greater than 0.`;
  return null;
}

// ---------------------------------------------------------------- lease
/**
 * California caps most residential security deposits at one month's rent (Civil Code 1950.5, as amended by AB 12,
 * operative 1 July 2024; certain small landlords may collect up to two months) and requires the deposit back within
 * 21 days. Source: leginfo.legislature.ca.gov, bill AB 12 (2023-24); bill text read 25 Oct 2026. Other states are not
 * guessed: the page tells the landlord to check their state's limit.
 */
export const DEPOSIT_RULE_SOURCE = "California Civil Code 1950.5 as amended by AB 12, operative July 1, 2024 (leginfo.legislature.ca.gov), as of Oct 25, 2026";

export function stateDepositReturnDays(state: string): number | null {
  return state === "California" ? 21 : null;
}

export function depositWarning(state: string, rent: number, deposit: number): string | null {
  if (state === "California" && rent > 0 && deposit > rent) {
    return "California generally caps a security deposit at one month's rent (up to two months only for certain small landlords). This deposit is higher than one month's rent, so check Civil Code 1950.5 before you use it.";
  }
  return null;
}

const ABBR: Record<string, string> = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California", CO: "Colorado", CT: "Connecticut", DE: "Delaware", DC: "District of Columbia",
  FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky", LA: "Louisiana", ME: "Maine",
  MD: "Maryland", MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi", MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada",
  NH: "New Hampshire", NJ: "New Jersey", NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota", OH: "Ohio", OK: "Oklahoma", OR: "Oregon",
  PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia",
  WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
};

/** The state an address appears to be in (a state name at the end, or "TX" before/after a ZIP), or null if unclear. */
export function stateInAddress(address: string): string | null {
  const a = address.trim();
  if (!a) return null;
  const abbr = /(?:^|[,\s])([A-Z]{2})(?:\s+\d{5}(?:-\d{4})?)?\s*$/.exec(a) ?? /(?:,|\s)([A-Z]{2})\s+\d{5}\b/.exec(a);
  if (abbr && ABBR[abbr[1]]) return ABBR[abbr[1]];
  const lower = a.toLowerCase().replace(/[.,]/g, " ").replace(/\s+/g, " ").replace(/\s+\d{5}(-\d{4})?$/, "").trim();
  // Longest names first so "West Virginia" wins over "Virginia".
  for (const st of [...US_STATES].sort((x, y) => y.length - x.length)) {
    if (lower.endsWith(" " + st.toLowerCase()) || lower === st.toLowerCase()) return st;
  }
  return null;
}

export function addressStateMismatch(address: string, state: string): string | null {
  if (!state) return null;
  const found = stateInAddress(address);
  return found && found !== state ? `The address looks like it is in ${found}, but the state chosen is ${state}. Check both.` : null;
}

export function wholeNumberProblem(raw: string, label: string, min: number, max?: number): string | null {
  if (!raw.trim()) return `${label}: enter a number.`;
  const n = Number(raw);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return `${label}: enter a whole number.`;
  if (n < min) return `${label}: must be ${min} or more.`;
  if (max !== undefined && n > max) return `${label}: must be ${max} or less.`;
  return null;
}

export function moneyProblem(raw: string, label: string, opts: { allowZero?: boolean } = {}): string | null {
  if (!raw.trim()) return null;
  const n = parseNumber(raw);
  if (n === null) return `${label}: enter an amount as a number.`;
  if (n < 0 || (n === 0 && !opts.allowZero)) return `${label}: must be ${opts.allowZero ? "0 or more" : "greater than 0"}.`;
  return null;
}

// ---------------------------------------------------------------- offer letter
export function salaryProblem(raw: string, label = "Salary"): string | null {
  if (!raw.trim()) return null;
  const n = parseNumber(raw);
  if (n === null) return `${label}: enter an amount as a number.`;
  if (n <= 0) return `${label} must be greater than 0.`;
  return null;
}

/** Start date not far in the past; response deadline not before today and not after the start date. */
export function offerDateProblems(startDate: string, responseDeadline: string, now?: string): { start?: string; deadline?: string } {
  const out: { start?: string; deadline?: string } = {};
  const t = todayYmd(now);
  const s = parseLocalDate(startDate.trim());
  const d = parseLocalDate(responseDeadline.trim());
  if (startDate.trim() && !s) out.start = "Enter a valid start date.";
  else if (s && cmp(s, t) < 0) out.start = "The start date is in the past. Pick today or a later date.";
  if (responseDeadline.trim() && !d) out.deadline = "Enter a valid date.";
  else if (d && cmp(d, t) < 0) out.deadline = "The response deadline is in the past. Pick today or a later date.";
  else if (d && s && cmp(d, s) > 0) out.deadline = "The response deadline should be on or before the start date.";
  return out;
}

// ---------------------------------------------------------------- cover letter
export const looksLikeEmailOrPhone = (s: string) => {
  const t = s.trim();
  if (/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(t)) return true;
  const digits = t.replace(/\D/g, "");
  return /^[+()\d\s.\-x]+$/i.test(t) && digits.length >= 7 && digits.length <= 15;
};

const LINKEDIN = /^(https?:\/\/)?([a-z]{2,3}\.)?linkedin\.com\/(in|pub)\/[\w%\-./]+$/i;
/** A real way to reach someone: an email address, a phone number or a LinkedIn profile address. A city or street line is not one. */
export const looksLikeContact = (s: string) => looksLikeEmailOrPhone(s) || LINKEDIN.test(s.trim());

/** Trim spaces and trailing full stops / commas / semicolons. */
export const cleanPhrase = (s: string) => s.trim().replace(/\s+/g, " ").replace(/[.,;:!\s]+$/, "");

/** If the user typed it all in lower case, capitalise each word; otherwise leave their capitalisation alone. */
export function titleIfLower(s: string): string {
  const t = s.trim();
  if (!t || t !== t.toLowerCase()) return t;
  return t.replace(/(^|[\s\-/(])(\S)/g, (_m, p, c) => p + c.toUpperCase());
}
